import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Spin } from 'antd'
import { useSelector } from 'react-redux'
import { useTranslation } from 'src/utils/i18n'
import { EXTREME_UPGRADED_EVENT } from 'src/constants/extremeUpgrade'
import { isGraceDevice } from 'src/utils/subscriptionStatus'
import { EXTREME_UPGRADE_FLAT_ICONS, EXTREME_UPGRADE_GROUPS } from './extremeUpgradeSupport'
import ExtremeModal from 'src/@core/components/modal/extreme'
import useExtremeUpgradePayment from './useExtremeUpgradePayment'

const chunkIcons = (items, size) => {
  const rows = []

  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size))
  }

  return rows
}

const SupportIcon = ({ item, compact = false }) => (
  <div className={compact ? 'flex size-10 shrink-0 items-center justify-center' : 'flex w-[103px] flex-col items-center gap-1'}>
    <div className={`relative overflow-hidden ${compact ? 'size-10 rounded-[10px]' : 'size-12 rounded-[12px]'}`}>
      <Image src={item.img} alt={item.name} fill className='object-contain' sizes={compact ? '40px' : '48px'} />
    </div>
    {compact ? null : (
      <span className='w-full text-center text-[14px] leading-5 text-[#353746]'>{item.name}</span>
    )}
  </div>
)

const HeroCopy = ({ titleClass, subtitleWrapClass = '' }) => {
  const { t } = useTranslation('common')

  return (
    <div className={`relative z-[1] flex flex-col items-center gap-2.5 ${subtitleWrapClass}`}>
      <h2 className={titleClass}>{t('Upgrade to Extreme Plan')}</h2>
      <p className='text-center text-[14px] leading-5 text-[#E8EDFF]'>
        {t('Unlock 19 advanced monitoring features and full')}{' '}
        <span className='inline-flex items-center gap-1 text-[16px] font-medium leading-6 text-white'>
          {t('iOS 26 support')}.
          <Image src='/images/extreme-upgrade/ios26-apple.svg' alt='' width={16} height={18} className='h-[18px] w-4' />
        </span>
      </p>
    </div>
  )
}

const PcHero = () => (
  <div className='relative flex h-[122px] items-center justify-center overflow-hidden rounded-[16px] px-8 py-6'>
    <Image
      src='/images/extreme-upgrade/hero-bg.svg'
      alt=''
      fill
      priority
      sizes='720px'
      className='pointer-events-none object-cover'
    />
    <HeroCopy titleClass='text-[24px] font-semibold leading-9 text-white' />
  </div>
)

const MobileHero = () => (
  <div className='relative overflow-hidden rounded-[12px] p-2.5'>
    <Image
      src='/images/extreme-upgrade/hero-bg-mobile.svg'
      alt=''
      fill
      sizes='(max-width: 990px) calc(100vw - 32px), 331px'
      className='pointer-events-none object-cover'
    />
    <HeroCopy
      titleClass='text-[20px] font-semibold leading-7 text-white'
      subtitleWrapClass='w-full'
    />
  </div>
)

const ExtremeUpgradeModal = ({ open, onClose }) => {
  const { t } = useTranslation('common')
  const selectedDeviceId = useSelector(state => state.Common.deviceUser)
  const deviceSwitchRevision = useSelector(state => state.Common.deviceSwitchRevision)
  const [mounted, setMounted] = useState(false)
  const [paidClosed, setPaidClosed] = useState(false)
  const [paymentFallbackOpen, setPaymentFallbackOpen] = useState(false)
  const [expiredBlocksUpgrade, setExpiredBlocksUpgrade] = useState(false)
  const { contextHolder, handleUnlock, loading, paying } = useExtremeUpgradePayment({
    enabled: open && !paidClosed && !expiredBlocksUpgrade,
    onPaymentFallback: () => setPaymentFallbackOpen(true)
  })

  const prevDeviceIdRef = useRef(selectedDeviceId)

  useEffect(() => {
    setMounted(true)
  }, [])

  // 宽限期过期续费优先：过期时不展示升级弹窗
  useEffect(() => {
    try {
      const deviceUser = JSON.parse(localStorage.getItem('DeviceUser') || 'null')
      setExpiredBlocksUpgrade(isGraceDevice(deviceUser))
    } catch {
      setExpiredBlocksUpgrade(false)
    }
  }, [selectedDeviceId, deviceSwitchRevision, open])

  useEffect(() => {
    if (!open) {
      setPaidClosed(false)
      setPaymentFallbackOpen(false)
    }
  }, [open])

  useEffect(() => {
    if (prevDeviceIdRef.current === selectedDeviceId) return

    prevDeviceIdRef.current = selectedDeviceId
    setPaidClosed(false)
    setPaymentFallbackOpen(false)
  }, [selectedDeviceId])

  useEffect(() => {
    const closeAfterPaid = () => {
      setPaidClosed(true)
      setPaymentFallbackOpen(false)
    }

    window.addEventListener(EXTREME_UPGRADED_EVENT, closeAfterPaid)

    return () => window.removeEventListener(EXTREME_UPGRADED_EVENT, closeAfterPaid)
  }, [])

  if (!open || !mounted || expiredBlocksUpgrade) return null

  const overlay = createPortal(
    <div className='fixed bottom-0 right-0 top-[60px] z-[1200] flex items-center justify-center overflow-y-auto bg-[rgba(0,0,0,0.5)] px-4 py-4 smj:left-0 smj:items-start smj:px-4 smj:pb-4 smj:pt-3 smjs:left-[300px]'>
      <div className='relative my-auto w-full max-w-[786px] rounded-[16px] bg-gradient-to-b from-white to-[#E8F4FF] p-8 shadow-[0px_6px_20px_0px_rgba(40,41,56,0.15)] smj:my-0 smj:max-h-[calc(100dvh-60px-28px)] smj:overflow-y-auto smj:px-4 smj:py-6'>
        {/* 关闭入口按需启用：传入 onClose 才渲染，未传的调用方（功能页）保持原有不可关闭行为 */}
        {onClose ? (
          <button
            type='button'
            aria-label='Close'
            onClick={onClose}
            className='absolute right-3 top-3 z-10 flex size-7 items-center justify-center rounded-full bg-[#020417] text-[16px] leading-none text-white transition-opacity hover:opacity-80'
          >
            ×
          </button>
        ) : null}
        <div className='flex flex-col items-center gap-8 smj:gap-8'>
          <div className='flex w-full flex-col gap-6 smj:gap-4'>
            <div className='smj:hidden'>
              <PcHero />
            </div>
            <div className='hidden smj:block'>
              <MobileHero />
            </div>

            <div className='flex flex-col gap-4'>
              <div className='flex items-center gap-1.5'>
                <h3 className='text-[20px] font-semibold leading-7 text-[#020417] smj:text-[18px] smj:font-medium smj:leading-[26px]'>
                  {t('Support')}
                </h3>
                <div className='hidden items-center gap-1.5 smj:flex'>
                  <span className='rounded-[6px] bg-[rgba(70,125,255,0.2)] px-2.5 text-[10px] leading-[18px] text-[#467dff]'>
                    {t('19 types')}
                  </span>
                  <span className='rounded-[6px] bg-[rgba(70,125,255,0.2)] px-2.5 text-[10px] leading-[18px] text-[#467dff]'>
                    {t('iOS26')}
                  </span>
                </div>
              </div>

              <div className='smj:hidden'>
                {EXTREME_UPGRADE_GROUPS.map(group => (
                  <div key={group.titleKey} className='mb-4 last:mb-0'>
                    <p className='mb-2.5 text-[16px] font-medium leading-6 text-[#020417]/80'>{t(group.titleKey)}</p>
                    <div className='flex flex-wrap gap-x-4 gap-y-2.5'>
                      {group.items.map(item => (
                        <SupportIcon key={item.name} item={item} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className='hidden w-full flex-col gap-4 smj:flex'>
                {chunkIcons(EXTREME_UPGRADE_FLAT_ICONS, 5).map(row => (
                  <div key={row.map(item => item.name).join('-')} className='flex w-full items-start justify-between'>
                    {row.map(item => (
                      <SupportIcon key={item.name} item={item} compact />
                    ))}
                    {row.length < 5
                      ? Array.from({ length: 5 - row.length }).map((_, index) => (
                          <div key={`spacer-${index}`} className='size-10 shrink-0' aria-hidden />
                        ))
                      : null}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <button
            type='button'
            disabled={paying || loading}
            className='relative mx-auto flex h-10 w-[240px] items-center justify-center rounded-[40px] bg-[#FFCF55] px-10 font-[Poppins] text-[14px] font-medium leading-5 tracking-normal text-[#020417] disabled:cursor-not-allowed disabled:opacity-70 smj:w-full'
            onClick={handleUnlock}
          >
            {paying ? <Spin size='small' /> : t('Unlock All Data')}
            <span
              className='pointer-events-none absolute -right-2 -top-[14px] box-border flex h-[29px] min-w-16 items-start justify-center whitespace-nowrap px-2.5 pt-1 text-center text-[12px] font-semibold uppercase leading-4 text-white smj:hidden'
              style={{
                backgroundImage: "url('/images/extreme-upgrade/cta-10-off-badge.svg')",
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat'
              }}
            >
              {t('10% off')}
            </span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  )

  return (
    <>
      {contextHolder}
      {paidClosed ? null : overlay}
      <ExtremeModal
        extremeOpen={paymentFallbackOpen && !paidClosed}
        setExtremeOpen={setPaymentFallbackOpen}
        zIndex={1300}
      />
    </>
  )
}

export default ExtremeUpgradeModal
